import { Tag } from 'fave-ui';
import React, { useMemo } from 'react';
import { StatusType } from '../../types/dataTypes';
import './style.module.css';
import { SettlementStatus } from '../../services/merchantSettlement';

type StatusPropsType = {
  status: StatusType | string;
  getStatusColor?: (status: StatusType | string) => string;
};

const statusColorMap = {
  Live: 'success',
  Active: 'success',
  Scheduled: 'warning',
  Inactive: 'default',
  Draft: '#198cf4',
};

const getStatusColorFromMap =
  <T extends string>(colorMap: { [key in T]: T }) =>
  (status: T): string =>
    colorMap[status] || 'default';

export const StatusTag: React.FC<StatusPropsType> = ({
  status,
  getStatusColor = getStatusColorFromMap(statusColorMap),
}) => (
  <Tag className={getStatusColor(status)} color={getStatusColor(status)}>
    {status}
  </Tag>
);

const merchantStatusColorMap = {
  Delist: 'warning',
  'Qa Not Approved': 'warning',
  Fraud: 'error',
  Test: 'warning',
  'Out Of Business': 'warning',
  Duplicate: 'warning',
  Suspicious: 'warning',
  Active: 'success',
};

export const MerchantStatusTag: React.FC<StatusPropsType> = ({ status }) => {
  const getMerchantStatusColor = useMemo(
    () => getStatusColorFromMap(merchantStatusColorMap),
    [],
  );

  return (
    <StatusTag
      status={status ?? 'Active'}
      getStatusColor={getMerchantStatusColor}
    />
  );
};

const onOffStatusColorMap = {
  Enabled: 'success',
  Disabled: 'error',
};

export const OnOffStatusTag: React.FC<StatusPropsType> = ({ status }) => {
  const getOnOffStatusColor = useMemo(
    () => getStatusColorFromMap(onOffStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getOnOffStatusColor} />;
};

const campaignStatusColorMap = {
  Active: 'success',
  Scheduled: 'warning',
  Inactive: 'default',
  Archived: 'error',
};

export const CampaignStatusTag: React.FC<StatusPropsType> = ({ status }) => {
  const getCampaignStatusColor = useMemo(
    () => getStatusColorFromMap(campaignStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getCampaignStatusColor} />;
};

const bannerStatusColorMap = {
  active: 'success',
  inactive: 'default',
};

export const BannerStatusTag: React.FC<StatusPropsType> = ({ status }) => {
  const getBannerStatusColor = useMemo(
    () => getStatusColorFromMap(bannerStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getBannerStatusColor} />;
};

const ippApprovalStatusColorMap = {
  Approved: 'success',
  Rejected: 'error',
  'In Progress': 'default',
  Pending: 'default',
  'N/A': 'default',
};

export const IPPApprovalStatusTag: React.FC<StatusPropsType> = ({ status }) => {
  const getStatusColor = useMemo(
    () => getStatusColorFromMap(ippApprovalStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getStatusColor} />;
};

const mySubmissionStatusColorMap = {
  'Pending signature': 'default',
  'Pending Ack.': 'default',
  Completed: 'success',
  Cancelled: 'error',
  Signed: 'success',
};

export const MySubmissionsStatusTag: React.FC<StatusPropsType> = ({
  status,
}) => {
  const getStatusColor = useMemo(
    () => getStatusColorFromMap(mySubmissionStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getStatusColor} />;
};

const makerCheckerStatusColorMap = {
  Pending: 'pending',
  Rejected: 'error',
  Completed: 'success',
  Approved: 'warning',
  Cancelled: 'default',
};

export const MakerCheckerStatusTag: React.FC<StatusPropsType> = ({
  status,
}) => {
  const getStatusColor = useMemo(
    () => getStatusColorFromMap(makerCheckerStatusColorMap),
    [],
  );

  return <StatusTag status={status} getStatusColor={getStatusColor} />;
};

export const MerchantSettlementStatusColorMap: {
  [key in SettlementStatus]: string;
} = {
  'Pending Review': 'default',
  'Checker Checked': 'warning',
  'Checker Rejected': 'error',
  'Reviewer Rejected': 'error',
  'Approver Rejected': 'error',
  'Approver Pending': 'warning',
  'Pending Bank': 'warning',
  'Settlement in Progress': 'warning',
  L2_ERROR: 'error',
  L3_ERROR: 'error',
  L3_REJECTION: 'error',
  L4_ERROR: 'error',
  L4_INCOMPLETE: 'error',
  'Bank Rejected': 'error',
  'Partial Bank Rejection': 'error',
  Completed: 'success',
};

export const MerchantSettlementStatusTag: React.FC<StatusPropsType> = ({
  status: statusKey,
  children: status,
}) => {
  const getStatusColor = useMemo(
    () => getStatusColorFromMap(MerchantSettlementStatusColorMap),
    [],
  );

  return (
    <Tag
      className={getStatusColor(statusKey)}
      color={getStatusColor(statusKey)}
    >
      {status}
    </Tag>
  );
};
